// SEO Services page with growth-focused copy
import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const seoService = {
  name: "SEO Services",
  promise: "Rank higher and attract more qualified organic traffic",
  primaryKPI: "+150% organic traffic in 6 months",
  signals: {
    bestFor: "Companies ready to invest in long-term organic growth",
    timeline: "3-6 months for results",
    team: "SEO strategist + technical SEO specialist",
    deliverables: "SEO strategy + technical optimization + content plan"
  },
  outputs: [
    "Complete SEO audit and competitive analysis",
    "Keyword research and content strategy",
    "Technical SEO fixes (speed, structure, schema)",
    "On-page optimization for target pages",
    "Local SEO setup (if applicable)",
    "Link building and outreach strategy",
    "Monthly performance reports with insights",
    "Google Analytics and Search Console setup"
  ],
  process: [
    {
      step: "Audit & Strategy",
      description: "Analyze current performance and identify opportunities",
      ownership: "We audit, you provide access to analytics",
      artifacts: ["SEO audit report", "Keyword strategy", "Competitor analysis"]
    },
    {
      step: "Technical Optimization",
      description: "Fix technical issues that prevent good rankings",
      ownership: "We optimize, you approve changes",
      artifacts: ["Site speed improvements", "Schema markup", "Technical fixes"]
    },
    {
      step: "Content & On-Page",
      description: "Optimize existing content and create new content",
      ownership: "We optimize, you review content",
      artifacts: ["Optimized pages", "Content calendar", "Meta descriptions"]
    },
    {
      step: "Monitor & Improve",
      description: "Track rankings and refine strategy based on results",
      ownership: "We monitor, you provide feedback",
      artifacts: ["Monthly reports", "Ranking improvements", "Next steps plan"]
    }
  ],
  proof: [
    {
      client: "Professional services firm",
      challenge: "Invisible in search results for target keywords",
      result: "Full-scope SEO strategy with technical and content optimization",
      metric: "Page 1 rankings for 23 target keywords"
    },
    {
      client: "E-commerce retailer",
      challenge: "High bounce rate from organic traffic",
      result: "Improved search intent matching and page experience",
      metric: "+89% organic conversion rate"
    }
  ],
  faqs: [
    {
      question: "How long before we see SEO results?",
      answer: "Technical improvements show in 4-8 weeks. Content and ranking improvements typically take 3-6 months to be significant."
    },
    {
      question: "What's included in your SEO service?",
      answer: "Technical fixes, keyword research, content optimization, local SEO (if applicable), and monthly reporting. No link buying."
    },
    {
      question: "Do you guarantee first page rankings?",
      answer: "We don't guarantee specific rankings, but we do guarantee improved organic visibility and traffic within 6 months."
    },
    {
      question: "How do you choose which keywords to target?",
      answer: "We analyze search volume, competition, and business value to target keywords that will drive qualified traffic and conversions."
    },
    {
      question: "Do you work with our existing content team?",
      answer: "Yes, we provide content briefs and optimization guidelines that your team can execute, or we can handle content creation."
    },
    {
      question: "How do you measure SEO success?",
      answer: "We track organic traffic, keyword rankings, click-through rates, and most importantly, conversions from organic search."
    }
  ]
};

const SEOServices = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="SEO Services - Increase Organic Traffic & Rankings | AffinityAim"
        description="Professional SEO services driving 156% organic traffic growth. Technical SEO, content strategy, link building. See results in 3-6 months."
        canonical="https://www.affinityaim.com/services/seo-services"
        keywords="SEO services, search engine optimization, technical SEO, organic traffic growth"
        type="service"
      />
      <Navigation />
      <ServiceDetailPage service={seoService} />
      <Footer />
    </div>
  );
};

export default SEOServices;